// app/javascript/controllers/price_chart_controller.js
import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from 'chart.js'

// Register Chart.js components
Chart.register(...registerables)

export default class extends Controller {
  static targets = ["canvas", "metricSelector"]
  static values = {
    data: { type: Array, default: [] },
    earnings: { type: Array, default: [] },
    range: { type: String, default: '1y' },
    interactive: { type: Boolean, default: true }
  }

  // Professional color scheme
  static colors = {
    positive: {
      line: '#9C5434',
      background: '#EBDED7',
      backgroundAlpha: 'rgba(235, 222, 215, 0.3)'
    },
    negative: {
      line: '#dc2626',
      background: '#fef2f2',
      backgroundAlpha: 'rgba(254, 242, 242, 0.3)'
    },
    neutral: {
      grid: 'rgba(107, 114, 128, 0.08)',
      text: '#6b7280',
      tooltip: 'rgba(17, 24, 39, 0.95)'
    }
  }

  connect() {
    console.log("Price chart controller connected!")
    console.log("Canvas target:", this.hasCanvasTarget)
    console.log("Data value:", this.dataValue)
    console.log("Earnings value:", this.earningsValue)
    console.log("Has earnings:", this.earningsValue && this.earningsValue.length > 0)

    // Initialize current metric (default to both)
    this.currentMetric = 'both'

    // Hide the fallback table when JS loads
    const fallback = this.element.querySelector('.price-table-fallback')
    if (fallback) {
      fallback.style.display = 'none'
    }

    // Initialize chart on next tick to ensure DOM is ready
    requestAnimationFrame(() => this.initializeChart())
  }

  // Handle metric selector change
  changeMetric(event) {
    this.currentMetric = event.target.value
    console.log("Metric changed to:", this.currentMetric)

    // Rebuild the entire chart with new metric datasets
    this.initializeChart()
  }

  rangeValueChanged() {
    console.log("Range changed to:", this.rangeValue)
    if (this.chart) {
      this.updateChart()
    }
  }

  disconnect() {
    this.destroyChart()
  }

  dataValueChanged() {
    if (this.chart) {
      this.updateChart()
    } else {
      this.initializeChart()
    }
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  getColorScheme(isPositive) {
    const colors = this.constructor.colors
    return isPositive ? colors.positive : colors.negative
  }

  initializeChart() {
    console.log("Initializing chart...")
    
    if (!this.hasCanvasTarget) {
      console.error("No canvas target found!")
      return
    }
    
    if (!this.dataValue || this.dataValue.length === 0) {
      console.error("No data available:", this.dataValue)
      return
    }

    // IMPORTANT: Destroy existing chart before creating new one
    this.destroyChart()
    
    const ctx = this.canvasTarget.getContext('2d')
    
    try {
      // Prepare data for Chart.js
      const labels = this.dataValue.map(record => {
        const date = new Date(record.timestamp)
        return this.formatDateLabel(date)
      })
      
      const prices = this.dataValue.map(record => parseFloat(record.close))
      
      // Calculate color based on price trend
      const firstPrice = prices[0]
      const lastPrice = prices[prices.length - 1]
      const isPositive = lastPrice >= firstPrice
      const colorScheme = this.getColorScheme(isPositive)
      const priceAxisBounds = this.calculatePriceAxisBounds(prices)

      // Prepare datasets array
      const datasets = [{
        label: 'Price',
        data: prices,
        borderColor: colorScheme.line,
        backgroundColor: colorScheme.backgroundAlpha,
        borderWidth: 2,
        fill: true,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: colorScheme.line,
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 2,
        yAxisID: 'y'
      }]

      // Add earnings dataset(s) based on selected metric
      if (this.earningsValue && this.earningsValue.length > 0) {
        const metric = this.currentMetric || 'operating_income'

        // Add Operating Income if selected or in 'both' mode
        if (metric === 'operating_income' || metric === 'both') {
          const operatingIncomeData = this.prepareEarningsData(labels, 'operating_income')
          const pointRadii = this.prepareEarningsPointRadii(labels) // Show dots on reporting dates
          datasets.push({
            label: 'Operating Income',
            data: operatingIncomeData,
            borderColor: '#3b82f6', // Blue for operating income
            backgroundColor: 'transparent',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointRadius: pointRadii, // Variable point radius
            pointHoverRadius: 6,
            pointBackgroundColor: '#3b82f6',
            pointHoverBackgroundColor: '#3b82f6',
            pointBorderColor: '#3b82f6', // Blue border to match line (no white gap)
            pointBorderWidth: 2,
            pointHoverBorderColor: '#3b82f6',
            pointHoverBorderWidth: 2,
            yAxisID: 'y1',
            cubicInterpolationMode: 'monotone' // Smooth interpolation
          })
        }

        // Add Revenue if selected or in 'both' mode (as bar chart - only on reporting dates)
        if (metric === 'revenue' || metric === 'both') {
          const revenueData = this.prepareEarningsBarData(labels, 'revenue')
          datasets.push({
            type: 'bar', // Bar chart for revenue
            label: 'Revenue',
            data: revenueData,
            backgroundColor: 'rgba(34, 197, 94, 0.85)', // Always vibrant green
            borderColor: '#22c55e', // Vibrant green border
            borderWidth: 1.5,
            yAxisID: 'y1',
            barThickness: 35, // Fixed 35px wide bars, centered on reporting date
            borderSkipped: false // Draw border on all sides
          })
        }

        // Add Net Income if selected
        if (metric === 'net_income') {
          const netIncomeData = this.prepareEarningsData(labels, 'net_income')
          datasets.push({
            label: 'Net Income',
            data: netIncomeData,
            borderColor: '#54892E', // Green for net income
            backgroundColor: 'transparent',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointBackgroundColor: '#54892E',
            pointHoverBackgroundColor: '#54892E',
            pointHoverBorderColor: '#ffffff',
            pointHoverBorderWidth: 2,
            yAxisID: 'y1',
            cubicInterpolationMode: 'monotone'
          })
        }
      }

      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          events: this.interactiveValue ? ['mousemove', 'mouseout', 'click', 'touchstart', 'touchend'] : [],
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            legend: {
              display: this.earningsValue && this.earningsValue.length > 0,
              position: 'top',
              align: 'end',
              labels: {
                usePointStyle: true, // Use proper point styles (line/circle/rect) instead of boxes
                pointStyle: 'line', // Default style
                boxWidth: 40, // Wider to show line segments properly
                boxHeight: 3, // Line height
                padding: 15,
                font: {
                  size: 12,
                  weight: '500'
                },
                color: this.constructor.colors.neutral.text,
                generateLabels: (chart) => {
                  const datasets = chart.data.datasets
                  return datasets.map((dataset, i) => ({
                    text: dataset.label,
                    fillStyle: dataset.backgroundColor,
                    strokeStyle: dataset.borderColor,
                    lineWidth: 2,
                    hidden: !chart.isDatasetVisible(i),
                    index: i,
                    pointStyle: dataset.type === 'bar' ? 'rect' : 'line' // Rect for bars, line for line charts
                  }))
                }
              },
              onClick: (e, legendItem, legend) => {
                const index = legendItem.index
                const chart = legend.chart
                const meta = chart.getDatasetMeta(index)

                // Toggle visibility
                meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null
                chart.update()
              }
            },
            tooltip: {
              enabled: this.interactiveValue,
              backgroundColor: this.constructor.colors.neutral.tooltip,
              titleColor: '#ffffff',
              bodyColor: '#ffffff',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
              displayColors: false,
              cornerRadius: 8,
              padding: 12,
              callbacks: {
                title: (context) => {
                  const index = context[0].dataIndex
                  const record = this.dataValue[index]
                  const date = new Date(record.timestamp)
                  return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                },
                label: (context) => {
                  const value = context.parsed.y
                  const datasetLabel = context.dataset.label

                  // Format value based on dataset type
                  if (datasetLabel === 'Operating Income' || datasetLabel === 'Revenue' || datasetLabel === 'Net Income') {
                    // Format in billions for earnings metrics
                    const billions = (value / 1_000_000_000).toFixed(2)
                    return `${datasetLabel}: $${billions}B`
                  } else {
                    // Format as currency for price
                    return `${datasetLabel}: $${value.toFixed(2)}`
                  }
                },
                afterLabel: (context) => {
                  const index = context.dataIndex
                  const record = this.dataValue[index]
                  if (record.volume) {
                    return `Volume: ${this.formatVolume(record.volume)}`
                  }
                  return ''
                }
              }
            }
          },
          scales: {
            x: {
              display: true,
              grid: {
                display: false
              },
              ticks: {
                maxTicksLimit: this.getMaxTicks(),
                color: this.constructor.colors.neutral.text,
                font: {
                  size: 11
                }
              },
              border: {
                display: false
              }
            },
            y: {
              type: 'linear',
              position: 'right',
              min: priceAxisBounds.min,
              max: priceAxisBounds.max,
              grid: {
                color: this.constructor.colors.neutral.grid,
                drawBorder: false
              },
              ticks: {
                color: this.constructor.colors.neutral.text,
                font: {
                  size: 11
                },
                callback: (value) => `$${value.toFixed(2)}`,
                padding: 8
              },
              border: {
                display: false
              },
              title: {
                display: this.earningsValue && this.earningsValue.length > 0,
                text: 'Price',
                color: this.constructor.colors.neutral.text,
                font: {
                  size: 11,
                  weight: 'normal'
                }
              }
            },
            y1: {
              type: 'linear',
              position: 'left',
              display: this.earningsValue && this.earningsValue.length > 0,
              grid: {
                drawOnChartArea: false,
                drawBorder: false
              },
              ticks: {
                color: '#8C2F48',
                font: {
                  size: 11
                },
                callback: (value) => {
                  // Format in billions
                  if (value >= 1000000000) {
                    return `$${(value / 1000000000).toFixed(1)}B`
                  } else if (value >= 1000000) {
                    return `$${(value / 1000000).toFixed(1)}M`
                  }
                  return `$${value.toFixed(0)}`
                },
                padding: 8
              },
              border: {
                display: false
              },
              title: {
                display: true,
                text: 'Operating Income',
                color: '#8C2F48',
                font: {
                  size: 11,
                  weight: 'normal'
                }
              }
            }
          },
          elements: {
            point: {
              hoverRadius: 6
            }
          }
        }
      })
      
      console.log("Chart created successfully")
      
    } catch (error) {
      console.error("Error creating chart:", error)
      // Show fallback table on error
      const fallback = this.element.querySelector('.price-table-fallback')
      if (fallback) {
        fallback.style.display = 'block'
      }
    }
  }

  updateChart() {
    if (!this.chart || !this.dataValue || this.dataValue.length === 0) {
      console.log("No chart or data available for update")
      return
    }

    try {
      // Update labels and data
      const labels = this.dataValue.map(record => {
        const date = new Date(record.timestamp)
        return this.formatDateLabel(date)
      })
      
      const prices = this.dataValue.map(record => parseFloat(record.close))
      
      // Update color based on trend
      const firstPrice = prices[0]
      const lastPrice = prices[prices.length - 1]
      const isPositive = lastPrice >= firstPrice
      const colorScheme = this.getColorScheme(isPositive)
      const priceAxisBounds = this.calculatePriceAxisBounds(prices)
      
      this.chart.data.labels = labels
      this.chart.data.datasets[0].data = prices
      this.chart.data.datasets[0].borderColor = colorScheme.line
      this.chart.data.datasets[0].backgroundColor = colorScheme.backgroundAlpha
      this.chart.data.datasets[0].pointHoverBackgroundColor = colorScheme.line
      this.chart.options.scales.y.min = priceAxisBounds.min
      this.chart.options.scales.y.max = priceAxisBounds.max
      
      // Update x-axis max ticks based on range
      this.chart.options.scales.x.ticks.maxTicksLimit = this.getMaxTicks()
      
      this.chart.update('active')
      
    } catch (error) {
      console.error("Error updating chart:", error)
    }
  }

  formatDateLabel(date) {
    if (!this.rangeValue) return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

    switch (this.rangeValue) {
      case '1m':
      case '2m':
      case '3m':
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      case '6m':
      case '1y':
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      case '5y':
        return date.toLocaleDateString('en-US', { year: 'numeric' })
      default:
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
    }
  }

  getMaxTicks() {
    if (!this.rangeValue) return 10

    switch (this.rangeValue) {
      case '1m': return 8
      case '2m': return 10
      case '3m': return 10
      case '6m': return 12
      case '1y': return 12
      case '5y': return 6
      default: return 10
    }
  }

  formatVolume(volume) {
    if (!volume) return 'N/A'
    if (volume >= 1000000) {
      return (volume / 1000000).toFixed(1) + 'M'
    } else if (volume >= 1000) {
      return (volume / 1000).toFixed(1) + 'K'
    }
    return volume.toString()
  }

  calculatePriceAxisBounds(prices) {
    const numericPrices = prices.filter((price) => Number.isFinite(price))
    if (numericPrices.length === 0) {
      return { min: undefined, max: undefined }
    }

    const minPrice = Math.min(...numericPrices)
    const maxPrice = Math.max(...numericPrices)
    const spread = maxPrice - minPrice

    if (spread === 0) {
      const fallbackPadding = Math.max(Math.abs(maxPrice) * 0.03, 0.5)
      return {
        min: Math.max(0, minPrice - fallbackPadding),
        max: maxPrice + fallbackPadding
      }
    }

    const padding = Math.max(spread * 0.06, maxPrice * 0.005)
    return {
      min: Math.max(0, minPrice - padding),
      max: maxPrice + padding
    }
  }

  prepareEarningsData(priceLabels, metric = 'operating_income') {
    // Map earnings (quarterly) to daily price data points with smooth interpolation
    // metric can be: 'operating_income', 'revenue', or 'net_income'

    if (!this.earningsValue || this.earningsValue.length === 0) {
      return []
    }

    // Map metric name to earnings data field
    const metricField = metric === 'revenue' ? 'total_revenue' : metric

    // Sort earnings by date
    const sortedEarnings = [...this.earningsValue].sort((a, b) =>
      new Date(a.fiscal_date_ending) - new Date(b.fiscal_date_ending)
    )

    const earningsData = []

    // For each price point, find or interpolate the metric value
    this.dataValue.forEach((priceRecord) => {
      const priceDate = new Date(priceRecord.timestamp)

      // Find the earnings quarters before and after this price date
      let beforeEarning = null
      let afterEarning = null

      for (let i = 0; i < sortedEarnings.length; i++) {
        const earningDate = new Date(sortedEarnings[i].fiscal_date_ending)

        if (earningDate <= priceDate) {
          beforeEarning = sortedEarnings[i]
        } else if (earningDate > priceDate && !afterEarning) {
          afterEarning = sortedEarnings[i]
          break
        }
      }

      // Interpolate between quarters for smooth transitions
      if (beforeEarning && afterEarning) {
        const beforeDate = new Date(beforeEarning.fiscal_date_ending)
        const afterDate = new Date(afterEarning.fiscal_date_ending)
        const beforeValue = parseFloat(beforeEarning[metricField])
        const afterValue = parseFloat(afterEarning[metricField])

        // Linear interpolation
        const totalDays = (afterDate - beforeDate) / (1000 * 60 * 60 * 24)
        const daysSinceStart = (priceDate - beforeDate) / (1000 * 60 * 60 * 24)
        const ratio = daysSinceStart / totalDays

        const interpolatedValue = beforeValue + (afterValue - beforeValue) * ratio
        earningsData.push(interpolatedValue)
      } else if (beforeEarning) {
        // Use the last known value if no future data
        earningsData.push(parseFloat(beforeEarning[metricField]))
      } else if (afterEarning) {
        // Use the next value if no prior data
        earningsData.push(parseFloat(afterEarning[metricField]))
      } else {
        earningsData.push(null)
      }
    })

    return earningsData
  }

  prepareEarningsBarData(priceLabels, metric = 'revenue') {
    // Create bar chart data - only show bars on actual quarterly reporting dates
    // Find the CLOSEST price date to each earning report for proper alignment

    if (!this.earningsValue || this.earningsValue.length === 0) {
      return []
    }

    // Map metric name to earnings data field
    const metricField = metric === 'revenue' ? 'total_revenue' : metric

    // Initialize all data points as null (no bar)
    const barData = new Array(this.dataValue.length).fill(null)

    // For each earning report, find the closest price date
    this.earningsValue.forEach(earning => {
      if (!earning[metricField]) return

      const earningDate = new Date(earning.fiscal_date_ending)
      let closestIndex = -1
      let minDiff = Infinity

      // Find the price record with the closest date to this earning
      this.dataValue.forEach((priceRecord, index) => {
        const priceDate = new Date(priceRecord.timestamp)
        const diffDays = Math.abs((priceDate - earningDate) / (1000 * 60 * 60 * 24))

        // Only consider dates within 30 days and pick the closest one
        if (diffDays <= 30 && diffDays < minDiff) {
          minDiff = diffDays
          closestIndex = index
        }
      })

      // Place bar at the closest price date
      if (closestIndex !== -1) {
        barData[closestIndex] = parseFloat(earning[metricField])
      }
    })

    return barData
  }

  prepareEarningsPointRadii(priceLabels) {
    // Create point radius array - show bigger dots on quarterly reporting dates
    // Find the CLOSEST price date to each earning report (same logic as bars for alignment)
    if (!this.earningsValue || this.earningsValue.length === 0) {
      return []
    }

    // Initialize all radii as 0 (no dot)
    const radii = new Array(this.dataValue.length).fill(0)

    // For each earning report, find the closest price date
    this.earningsValue.forEach(earning => {
      const earningDate = new Date(earning.fiscal_date_ending)
      let closestIndex = -1
      let minDiff = Infinity

      // Find the price record with the closest date to this earning
      this.dataValue.forEach((priceRecord, index) => {
        const priceDate = new Date(priceRecord.timestamp)
        const diffDays = Math.abs((priceDate - earningDate) / (1000 * 60 * 60 * 24))

        // Only consider dates within 30 days and pick the closest one
        if (diffDays <= 30 && diffDays < minDiff) {
          minDiff = diffDays
          closestIndex = index
        }
      })

      // Place dot at the closest price date
      if (closestIndex !== -1) {
        radii[closestIndex] = 5 // 5px radius dot
      }
    })

    return radii
  }
}
